import { isEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { FormOrientationContext } from 'components/common-form/contexts/FormOrientationContext';
import { useDirectChildrenBlocksInvalidFields } from 'components/common-form/hooks/invalid-fields/useDirectChildrenBlocksInvalidFields';
import { useDirectChildrenBlocksPatchedFields } from 'components/common-form/hooks/patched-fields/useDirectChildrenBlocksPatchedFields';
import { useBlocksMetadata } from 'components/common-form/hooks/useBlockMetadata';
import { Tab } from 'components/shared/Tabs/Tab';
import { Tabs } from 'components/shared/Tabs/Tabs';
import type { IntlKey } from 'locales';
import type { BlockMetadata } from 'models/metadata/block.definition';
import { useCallback, useEffect, useMemo } from 'react';
import type { QuoteWorkspaceVersion } from 'state/userPreferences/userPreferences.model';

import { FieldCountBadges } from './components/FieldCountBadges';
import { ChildNode, FieldsList } from './FieldsList';
import type { TabsBlockProps } from './TabsBlock.connect';
import { connectTabsBlock } from './TabsBlock.connect';

// hack to have same id's between the Dynamic screen and the nss screen
// do not change this because it is expected by DEFENDER
function mapIdForDefender(tabId: string): string {
  if (tabId === 'RegulationTab') {
    return 'RegulationTabForQuote';
  }

  if (tabId === 'ProductCodesTab') {
    return 'LegalDataProductCodesTab';
  }

  return tabId;
}

type Visibility = 'shown' | 'hidden' | 'ghost';

function getBlockVisibility(
  m: BlockMetadata,
  quoteWorkspaceVersion: QuoteWorkspaceVersion,
  mustForceAdvancedMode: boolean,
  hiddenTabsIds: string[] = [],
  totalErrorCount: number,
): Visibility {
  if (m.isHidden === true) {
    return 'hidden';
  }
  if (hiddenTabsIds.includes(m.id)) {
    return totalErrorCount > 0 ? 'ghost' : 'hidden';
  }
  if (quoteWorkspaceVersion === 'Standard' && m.isForAdvancedMode === true && mustForceAdvancedMode === false) {
    return totalErrorCount > 0 ? 'ghost' : 'hidden';
  }
  return 'shown';
}
export function Component({
  tabId,
  level,
  node: { id, children },
  activeTab,
  workspaceType,
  hiddenTabsId,
  visibleTabsId,
  setActiveFormTab,
  quoteWorkspaceVersion,
  batchWorkspaceVersion,
  mustForceAdvancedMode,
}: TabsBlockProps) {
  const onTabActivated = useCallback(
    (value: any) => {
      setActiveFormTab(tabId, value, workspaceType);
    },
    [setActiveFormTab, tabId, workspaceType],
  );

  const allTabsId = useMemo(() => {
    if (visibleTabsId !== undefined && hiddenTabsId !== undefined) {
      return visibleTabsId.concat(hiddenTabsId);
    }
    return children?.map(c => c.id) ?? [];
  }, [children, hiddenTabsId, visibleTabsId]);

  const blocksMetadata = useBlocksMetadata(allTabsId);

  const blocksNodes = useMemo(
    () => blocksMetadata.map(m => children?.find(x => x.id === m.id)),
    [blocksMetadata, children],
  );
  const blocksErrorsCount = useDirectChildrenBlocksInvalidFields(blocksNodes);

  const blocksModifiedCount = useDirectChildrenBlocksPatchedFields(blocksNodes);

  const blocksVisibility = useMemo(() => {
    const blocksVisibility: Record<string, Visibility> = {};
    blocksMetadata.forEach((blockMetadata, index) => {
      blocksVisibility[blockMetadata.id] = getBlockVisibility(
        blockMetadata,
        quoteWorkspaceVersion,
        mustForceAdvancedMode,
        hiddenTabsId,
        blocksErrorsCount[index].totalCount,
      );
    });
    return blocksVisibility;
  }, [blocksMetadata, mustForceAdvancedMode, quoteWorkspaceVersion, hiddenTabsId, blocksErrorsCount]);

  const defaultBlockId = useMemo(() => {
    const firstVisibleBlockEntry = Object.entries(blocksVisibility).find(([_, visibility]) => visibility === 'shown');
    return firstVisibleBlockEntry ? firstVisibleBlockEntry[0] : undefined;
  }, [blocksVisibility]);

  useEffect(() => {
    if (activeTab !== undefined && blocksVisibility[activeTab] === 'hidden') {
      onTabActivated(undefined);
    }
  }, [activeTab, blocksVisibility, onTabActivated]);

  if (isEmpty(children)) {
    return null;
  }

  return (
    <div id={id} className="block">
      <div className="card shadow-sm bg-lvl1">
        <div className="card-body pt-0 pb-3">
          <div className="card-text">
            <Tabs id={id} activeTabId={activeTab ?? defaultBlockId} onTabActivated={onTabActivated}>
              {blocksMetadata.map(({ id: childTabId, isCustomized }, blockIndex) => {
                const blockNode = blocksNodes[blockIndex];
                const invalidCount = blocksErrorsCount[blockIndex];
                const modifiedCount = blocksModifiedCount[blockIndex];
                const visibility = blocksVisibility[childTabId];

                if (visibility === 'hidden') {
                  return null;
                }

                return (
                  <Tab
                    key={childTabId}
                    tabId={childTabId}
                    label={`form.${childTabId}` as IntlKey}
                    className={classNames(
                      {
                        'text-info': isCustomized,
                        'text-secondary font-italic': visibility === 'ghost',
                      },
                      'px-2 d-flex align-items-center',
                    )}
                    e2eId={mapIdForDefender(childTabId)}
                    afterLabel={<FieldCountBadges id={childTabId} value={invalidCount} modifiedCount={modifiedCount} />}
                    beforeLabel={isCustomized && <i className="icon icon-sm text-info">fiber_manual_record</i>}
                  >
                    <div className="pt-3">
                      <FormOrientationContext.Provider value={blockNode?.form ?? 'vertical'}>
                        <FieldsList
                          nodes={blockNode?.children}
                          direction={blockNode?.direction ?? 'leftToRight'}
                          blockWidth={blockNode?.width ?? 'full'}
                          renderChild={(childNode, defaultWidth) => (
                            <ChildNode
                              key={childNode.id}
                              tabId={tabId}
                              node={childNode}
                              level={level}
                              defaultWidth={defaultWidth}
                              workspaceType={workspaceType}
                              quoteWorkspaceVersion={quoteWorkspaceVersion}
                              batchWorkspaceVersion={batchWorkspaceVersion}
                              mustForceAdvancedMode={mustForceAdvancedMode}
                            />
                          )}
                        />
                      </FormOrientationContext.Provider>
                    </div>
                  </Tab>
                );
              })}
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}

export const TabsBlock = connectTabsBlock(Component);

Component.displayName = 'TabsBlock';
